import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import classnames from 'classnames';

import { ItemPrice } from './ItemPrice';
import { IncludedLabels } from './IncludedLabels';
import { SalePriceBanner } from '../SalePriceBanner';
import { useItemPriceData } from '../hooks/useItemPriceData';
import { useShowIncludedLabelsInline } from './hooks/useShowIncludedLabelsInline';

import { type PriceDisplayVariant_item$key } from './__generated__/PriceDisplayVariant_item.graphql';
import { type PriceDisplayVariant_displayPrice$key } from './__generated__/PriceDisplayVariant_displayPrice.graphql';

import dibsCss from 'dibs-css';

const itemFragment = graphql`
    fragment PriceDisplayVariant_item on Item {
        ...ItemPrice_item
        ...useItemPriceData_item
        ...IncludedLabels_item
        ...useShowIncludedLabelsInline_item
    }
`;

const displayPriceFragment = graphql`
    fragment PriceDisplayVariant_displayPrice on ItemDisplayPriceType @relay(plural: true) {
        ...ItemPrice_displayPrice
        ...useItemPriceData_displayPrice
        ...IncludedLabels_displayPrice
        ...useShowIncludedLabelsInline_displayPrice
    }
`;

export const PriceDisplayVariant: FC<{
    item?: PriceDisplayVariant_item$key | null;
    displayPrice?: PriceDisplayVariant_displayPrice$key | null;
    currency: string;
    isSaleUrgencyBannerVariant?: boolean;
}> = ({
    item: itemRef,
    displayPrice: displayPriceRef,
    currency,
    isSaleUrgencyBannerVariant = false,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const displayPrice = useFragment(displayPriceFragment, displayPriceRef);
    const showIncludedLabelsInline = useShowIncludedLabelsInline({
        item,
        displayPrice,
        currency,
    });
    const priceData = useItemPriceData({
        item,
        displayPrice,
        currency,
    });

    if (!priceData) {
        return null;
    }

    const { holdType, holdExpiration, privateOfferExpiration, currentPrice } = priceData;

    const showHoldExpiration = holdExpiration && holdType === 'HOLD_CUSTOM';
    const showPrivateOfferExpiration = privateOfferExpiration && !holdExpiration;

    return (
        <div data-tn="price-wrapper" className={dibsCss.wFull}>
            {isSaleUrgencyBannerVariant &&
                !showHoldExpiration &&
                !holdType &&
                !showPrivateOfferExpiration &&
                currentPrice.type === 'WEEKLY_SALE' &&
                'saleEndDate' in priceData &&
                priceData.saleEndDate && (
                    <SalePriceBanner
                        isPriceDisplayOptimizationVariant
                        saleEndDate={priceData.saleEndDate}
                    />
                )}
            {holdType && (
                <div className={classnames(dibsCss.sassyFontBodyMediumHeavy, dibsCss.textNoir)}>
                    {holdType === 'HOLD' && (
                        <FormattedMessage
                            id="PriceDisplayVariant.holdType"
                            defaultMessage="On Hold"
                        />
                    )}
                    {holdType === 'HOLD_CUSTOM' && (
                        <FormattedMessage
                            id="PriceDisplayVariant.holdTypeCustom"
                            defaultMessage="On Hold For You"
                        />
                    )}
                </div>
            )}

            {(showHoldExpiration || showPrivateOfferExpiration) && (
                <div
                    className={classnames({
                        [dibsCss.sassyFontBodySmall]: showHoldExpiration,
                        [dibsCss.textAlertsecondary]: showHoldExpiration,
                        [dibsCss.mt2px]: showHoldExpiration,
                        [dibsCss.sassyFontBodySmallHeavy]: showPrivateOfferExpiration,
                        [dibsCss.textNoir]: showPrivateOfferExpiration,
                        [dibsCss.mb2px]: showPrivateOfferExpiration,
                    })}
                >
                    <FormattedMessage
                        id="PriceDisplayVariant.expiration"
                        defaultMessage="Expires {time}"
                        values={{
                            time: showHoldExpiration ? holdExpiration : privateOfferExpiration,
                        }}
                    />
                </div>
            )}

            {(holdType || showHoldExpiration) && <div className={dibsCss.mbXsmall} />}

            <ItemPrice item={item} displayPrice={displayPrice} currency={currency} />

            {showIncludedLabelsInline && (
                <IncludedLabels item={item} displayPrice={displayPrice} currency={currency} />
            )}
        </div>
    );
};
